import { addressLike } from "@gearbox-protocol/cli-utils";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  useToast,
} from "@gearbox-protocol/deploy-tools-ui";
import {
  createAnvilClient,
  makePendingWithdrawalsClaimable,
} from "@gearbox-protocol/sdk/dev";
import { zodResolver } from "@hookform/resolvers/zod";
import type { FC } from "react";
import type { Resolver } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useUnpausedForkStatus } from "routes/fork/context";
import { useSDK } from "routes/fork/sdk-context";
import type { Address } from "viem";
import { http } from "viem";
import { z } from "zod";

import logger from "../../logger";

const schema = z.object({
  address: addressLike(),
});

type MakeWithdrawalsClaimableSchema = z.output<typeof schema>;

interface MakeWithdrawalsClaimableDialogProps {
  toggleOpen: (open: boolean) => void;
}

const MakeWithdrawalsClaimableDialog: FC<
  MakeWithdrawalsClaimableDialogProps
> = ({ toggleOpen }) => {
  const { toast } = useToast();
  const { rpcUrl } = useUnpausedForkStatus();
  const { value: sdk } = useSDK();

  const form = useForm<MakeWithdrawalsClaimableSchema>({
    resolver: zodResolver(schema) as Resolver<MakeWithdrawalsClaimableSchema>,
    defaultValues: {
      address: "" as string as Address,
    },
  });

  const submit = form.handleSubmit(async ({ address }) => {
    if (!sdk) {
      return;
    }
    try {
      const anvil = createAnvilClient({
        chain: sdk.chain,
        transport: http(rpcUrl, { timeout: 240_000 }),
      });
      await makePendingWithdrawalsClaimable(anvil, address, {
        logger,
      });
      toast({
        variant: "success",
        title: "Pending withdrawals are now claimable",
        description: address,
      });
      toggleOpen(false);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Failed to make pending withdrawals claimable",
        description: `${e}`,
      });
    }
  });

  const submitting = form.formState.isSubmitting;

  return (
    <Dialog open={true} onOpenChange={toggleOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Make withdrawals claimable</DialogTitle>
          <DialogDescription>
            Make pending delayed withdrawals claimable for a credit account or a
            single redeemer
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={submit} className="space-y-2">
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Credit account or redeemer</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="0x..."
                      autoComplete="off"
                      spellCheck={false}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        {submitting && (
          <p className="text-muted-foreground text-sm">
            Making pending withdrawals claimable, this may take a while...
          </p>
        )}

        <DialogFooter>
          <Button
            type="submit"
            disabled={submitting || !sdk}
            loading={submitting}
            onClick={submit}
          >
            Make claimable
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MakeWithdrawalsClaimableDialog;
